import {
  createContext,
  useEffect,
  useMemo,
  useState,
  useContext,
  useRef
} from 'react';
import isEqual from 'lodash/isEqual';
import { useSoyuzLocales, clientErrorTracker } from '@spotahome/soyuz/client';
import { FilterUtils, useFiltersContext } from '@spotahome/ui-library';
import { RESULTS_PER_PAGE_48 } from '@spotahome/ui-library/src/utils/searchResults';

import debounce from '@spotahome/ui-library/src/tenant/components/utils/debounce';
import getGqlClient from '@spotahome/ui-library/src/tenant/clients/graphql/marketplace';

import SimplifiedCardPaginator from '@spotahome/marketplace-common/src/lib/simplified-card-paginator';
import { requestSeoSearchData } from '@spotahome/marketplace-common/src/graphql/queries/search/seoSearchQuery';

const SeoSearchContext = createContext({});

const WAIT_TO_REQUEST = 150;

const SeoSearchProvider = ({
  children,
  initialHomecards,
  initialFilters,
  initialNMarkers,
  initialMarkers,
  cityId
}) => {
  const pageSize = RESULTS_PER_PAGE_48;

  const cardPaginator = useRef(
    new SimplifiedCardPaginator({
      initialPage: initialFilters?.page || 1,
      pageSize,
      numberOfMarkers: initialNMarkers || 0,
      initialMarkers: initialMarkers || []
    })
  );
  const prevFilters = useRef(initialFilters);
  const latestRequestId = useRef(0);
  const { current: currentLocale } = useSoyuzLocales();
  const { filters, handleUpdatePage } = useFiltersContext();
  const [isLoadingHomecards, setIsLoadingHomecards] = useState(false);
  const [homecards, setHomecards] = useState(initialHomecards);
  const [markers, setMarkers] = useState(initialMarkers || []);
  const [totalResults, setTotalResults] = useState(initialNMarkers);
  const [locationCoordinates, setLocationCoordinates] = useState(null);

  const executeSearch = async (filtersToRequest, { silent = false } = {}) => {
    const requestId = ++latestRequestId.current;

    if (!silent) {
      setIsLoadingHomecards(true);
    }

    try {
      const { data } = await requestSeoSearchData(getGqlClient())({
        cityId,
        locale: currentLocale,
        filters: FilterUtils.adaptFiltersFromUrl(filtersToRequest),
        limit: pageSize
      });

      if (requestId !== latestRequestId.current) return;

      const {
        search: { totalMarkers, markers: newMarkers, homecards: newHomecards },
        geocodeLocation
      } = data;

      setTotalResults(totalMarkers);
      setMarkers(newMarkers);
      setHomecards(newHomecards);
      cardPaginator.current.updateData({
        markers: newMarkers,
        nTotalMarkers: totalMarkers
      });

      if (geocodeLocation) {
        setLocationCoordinates(geocodeLocation);
      }
    } catch (error) {
      clientErrorTracker.error({
        error,
        source: 'SeoSearchContext-executeSearch'
      });
    } finally {
      if (!silent && requestId === latestRequestId.current) {
        setIsLoadingHomecards(false);
      }
    }
  };

  const debouncedSearch = useMemo(
    () => debounce(executeSearch, WAIT_TO_REQUEST),
    []
  );

  useEffect(() => {
    if (!initialHomecards?.length || !initialMarkers?.length) {
      executeSearch(initialFilters, { silent: true });
    }
  }, []);

  useEffect(() => {
    const { page: prevPage, ...restPrevFilters } = prevFilters.current;
    const { page, ...restFilters } = filters;

    if (!isEqual(restFilters, restPrevFilters) || page !== prevPage) {
      debouncedSearch(filters);
    }

    prevFilters.current = filters;
  }, [filters]);

  return (
    <SeoSearchContext.Provider
      value={{
        isLoadingHomecards,
        homecards,
        totalResults,
        currentMarkers: markers,
        paginator: {
          isPrevButtonDisabled:
            cardPaginator.current?.getPreviousPageNumber() === null,
          isNextButtonDisabled:
            cardPaginator.current?.getNextPageNumber() === null,
          handleNextPage: () => {
            const nextPage = cardPaginator.current?.getNextPageNumber();
            if (nextPage) {
              cardPaginator.current.setCurrentPage(nextPage);
              handleUpdatePage(nextPage);
            }
          },
          handlePrevPage: () => {
            const prevPage = cardPaginator.current?.getPreviousPageNumber();
            if (prevPage) {
              cardPaginator.current.setCurrentPage(prevPage);
              handleUpdatePage(prevPage);
            }
          },
          getCurrentPageNumber: () =>
            cardPaginator.current?.getCurrentPageNumber(),
          getTotalPages: () => cardPaginator.current?.getTotalPages(),
          getPreviousPageNumber: () =>
            cardPaginator.current?.getPreviousPageNumber(),
          getNextPageNumber: () => cardPaginator.current?.getNextPageNumber(),
          setCurrentPage: page => {
            cardPaginator.current?.setCurrentPage(page);
            handleUpdatePage(cardPaginator.current?.getCurrentPageNumber());
          }
        },
        locationCoordinates,
        cityId
      }}
    >
      {children}
    </SeoSearchContext.Provider>
  );
};

export const useSeoSearchContext = () => useContext(SeoSearchContext);

export const useSeoSearchMarkers = () => {
  const { currentMarkers } = useContext(SeoSearchContext);

  return currentMarkers;
};

export const useSeoSearchHomecards = () => {
  const { homecards } = useContext(SeoSearchContext);

  return homecards;
};

export const useSeoTotalSearchResults = () => {
  const { totalResults } = useContext(SeoSearchContext);

  return totalResults;
};

export const useSeoSearchPaginator = () => {
  const { paginator } = useContext(SeoSearchContext);

  return paginator;
};

export default SeoSearchProvider;
